import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  ViewChild
} from '@angular/core';
import type {EChartsOption, LineSeriesOption} from 'echarts';
import {SingleQuestionResponsesComponent} from '../single-question-responses/single-question-responses.component';
import {MatDialog} from '@angular/material/dialog';
import {SurveyService} from '../../../services/surveys.service';
import {TranslateService} from '@ngx-translate/core';
import mermaid, {MermaidConfig} from 'mermaid';

@Component({
  selector: 'app-question-history-dashboard',
  templateUrl: './question-history-dashboard.component.html',
  styleUrls: ['./question-history-dashboard.component.scss']
})
export class QuestionHistoryDashboardComponent implements OnInit, AfterViewInit {
  @Input() questionHistory: any;
  @Input() accordionOpened: boolean = true;
  @ViewChild('mermaid', {static: false}) mermaidDiv!: ElementRef;

  loading = false;
  chartOptions: EChartsOption = {};
  mermaidDefinition = null;
  currentSurvey = null;
  surveys = [];
  surveysToCompare = [];

  mermaidConfig: MermaidConfig = {
    startOnLoad: false,
    flowchart: {
      useMaxWidth: true,
      htmlLabels: true,
      curve: 'natural',
    },
    securityLevel: 'loose',
  };

  constructor(
    private dialog: MatDialog,
    private surveyService: SurveyService,
    protected translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) {
  }

  get question() {
    return this.questionHistory?.questions ? this.questionHistory.questions[0] : null;
  }

  ngOnInit(): void {
    if (this.questionHistory?.questions?.length) {
      this.surveys = this.questionHistory.questions.map((q) => ({
        ...q.survey,
        survey_question: q
      })).sort((a, b) =>
        a.major_version !== b.major_version
          ? a.major_version - b.major_version
          : a.minor_version - b.minor_version
      );
      this.currentSurvey = this.surveys[0];
      this.buildChartOptions();
    }
  }

  ngAfterViewInit(): void {
    this.renderMermaidDiagram();
  }

  toggleSurveyComparison(survey: any, checked: boolean) {
    if (checked) {
      this.surveysToCompare.push(survey);
    } else {
      this.surveysToCompare = this.surveysToCompare.filter(
        (s) => s.id !== survey.id
      );
    }
    this.surveysToCompare.sort((a, b) => {
      if (a.major_version === b.major_version) {
        return a.minor_version - b.minor_version;
      }
      return a.major_version - b.major_version;
    });
  }

  private buildChartOptions() {
    const questions = this.questionHistory.questions;
    let lastRev: number | null = null;

    const versions = questions.map((q: any) => {
      const [majorStr, minorStr] = q.survey_version_number.split('.');
      const major = Number(majorStr);
      const minor = Number(minorStr);
      const sum = major + minor;

      const rev = q.major_version - 1;

      const revLine = (rev !== lastRev)
        ? `REV ${rev}`
        : '';

      lastRev = rev;

      return `Session ${sum}${revLine ? `\n\n${revLine}` : ''}`;
    });


    if (this.surveyService.isConsensusQuestion(this.question?.typology)) {
      const metrics = [
        {
          name: this.translateService.instant('generic.certainty-degree'),
          value: 'certainty_degree',
        },
        {
          name: this.translateService.instant('generic.contradiction-degree'),
          value: 'contradiction_degree',
        },
        {
          name: this.translateService.instant('components.responses.consensus.agree'),
          value: 'a_ratio',
        }
      ];

      questions?.forEach((question) => {
        if (question?.results && Array.isArray(question?.responses) && question.responses.length > 0) {
          question.results.a_ratio =
            question.responses.filter((res) => res?.answer === 'AGREE').length / question.responses.length * 100;
        } else if (question?.results) {
          question.results.a_ratio = 0;
        }
      });

      const series: LineSeriesOption[] = metrics.map(metric => ({
        name: metric?.name,
        type: 'line',
        data: questions.map((q: any) => Number(q.results?.[metric?.value] ?? 0).toFixed(2))
      }));

      this.chartOptions = {
        tooltip: {
          trigger: 'axis',
          formatter: (params: any) => {
            return params
              .map((p: any) => `${p.marker}${p.seriesName}: ${p.value}%`)
              .join('<br/>');
          }
        },
        legend: {
          data: metrics.map(m => m.name),
          selectedMode: 'multiple'
        },
        grid: {left: '3%', right: '4%', bottom: '3%', containLabel: true},
        xAxis: {type: 'category', boundaryGap: false, data: versions},
        yAxis: {
          type: 'value',
          min: 0,
          max: 100,
          axisLabel: {formatter: '{value} %'}
        },
        series
      };
    }
    if (this.surveyService.isQuantitativeQuestion(this.question?.typology)) {
      const metrics = [
        {
          name: this.translateService.instant('generic.average'),
          value: 'mean',
        },
        {
          name: this.translateService.instant('generic.geometric-average'),
          value: 'geometric_mean',
        },
        {
          name: this.translateService.instant('generic.median'),
          value: 'median',
        },
        {
          name: this.translateService.instant('generic.variance'),
          value: 'variance',
        },
      ];

      const series: LineSeriesOption[] = metrics.map(metric => ({
        name: metric?.name,
        type: 'line',
        data: questions.map((q: any) => Number((q.quantitative_results?.[metric?.value] ?? 0)?.toFixed(2)))
      }));

      this.chartOptions = {
        tooltip: {trigger: 'axis'},
        legend: {
          data: metrics,
          selectedMode: 'multiple'
        },

        grid: {left: '3%', right: '4%', bottom: '3%', containLabel: true},
        xAxis: {type: 'category', boundaryGap: false, data: versions},
        yAxis: {type: 'value'},
        series
      };
    }
  }

  renderMermaidDiagram(): void {
    mermaid.initialize(this.mermaidConfig);

    const el = this.mermaidDiv?.nativeElement;

    if (el) {
      this.mermaidDefinition = this.generateMermaidDiagram();
      mermaid.render('generatedDiagram', this.mermaidDefinition).then((result) => {
        el.innerHTML = result.svg;
        // @ts-ignore
        result.bindFunctions?.();

        const svg = el.querySelector('svg');

        if (svg) {
          const nodes = svg.querySelectorAll('.node');
          svg.style.display = 'block';
          svg.style.margin = '0 auto';

          nodes.forEach(node => {
            node.addEventListener('click', () => {
              const rawId = node.id;
              const match = rawId.match(/S_(\d+)/);
              if (match) {
                const surveyId = parseInt(match[1], 10);
                this.onNodeClick(surveyId);
              }
            });

            node.addEventListener('mouseenter', () => {
              node.style.cursor = 'pointer';
            });

            node.addEventListener('mouseleave', () => {
              node.style.fill = ''; // reset al colore originale
            });
          });

        }
      }).catch(err => {
        console.error('Mermaid rendering error:', err);
      });
    }
  }

  onNodeClick(surveyID: number) {
    this.currentSurvey = this.surveys?.find((survey) => surveyID == survey?.id);
    this.renderMermaidDiagram();
  }

  generateMermaidDiagram(): string {
    const surveyMap = new Map<string, any>();
    this.surveys.forEach(s => surveyMap.set(`${s.major_version}.${s.minor_version}`, s));

    const nodes: string[] = [];
    const links: string[] = [];

    this.surveys.forEach(s => {
      const nodeId = `S_${s.id}`;
      const label = `Survey ${s.version_number}`;

      let styledNode = s.is_draft
        ? `${nodeId}["${label}"]`
        : `${nodeId}["${label}"]:::started`;

      if (s.id === this.currentSurvey?.id) {
        styledNode = `${nodeId}["${label}"]:::current`;
      }

      nodes.push(styledNode);

      if (s.minor_version > 0) {
        const prevMinorKey = `${s.major_version}.${s.minor_version - 1}`;
        const prevSurvey = surveyMap.get(prevMinorKey);
        if (prevSurvey) {
          links.push(`S_${prevSurvey.id} --> S_${s.id}`);
        }
      } else if (s.major_version > 1) {
        const prevMajorKey = `${s.major_version - 1}.0`;
        const prevSurvey = surveyMap.get(prevMajorKey);
        if (prevSurvey) {
          links.push(`S_${prevSurvey.id} --> S_${s.id}`);
        }
      }
    });

    const styleBlock = [
      'classDef started fill:#5C39A1,stroke:#333,color:#fff;',
      'classDef current fill:#1F9E99,stroke:#333,color:#fff;'
    ].join('\n');

    return ['flowchart LR', ...nodes, ...links, styleBlock].join('\n');
  }


  onChartClick(event: any) {
    const question = this.questionHistory?.questions[event?.dataIndex];
    this.dialog.open(SingleQuestionResponsesComponent, {
      panelClass: 'fullscreen-dialog',
      data: {
        questionID: question?.id,
        showPrivateInfo: true,
        isDialog: true
      }
    });
  }

  get timelineIsEnabled() {
    return !this.surveyService.isConsensusQuestion(this.question?.typology) &&
      !this.surveyService.isQuantitativeQuestion(this.question?.typology);
  }

  get currentQuestion() {
    return this.currentSurvey?.survey_question;
  }
}
